import { Injectable, Output, EventEmitter } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import * as CubiqServices from "shared/cubiq-services";
import { BfxSoundsService } from "../../bfx-common/services/bfx-sounds.service";
import { TranslateService } from "@ngx-translate/core";
import {
  BlotterFilterPromiseLookupService,
  ActiveUserProfilePromiseService,
  ProfilePromiseActionService,
  SystemConfigPromiseService,
} from "shared/cubiq-services";
import { ProfileSettingKeys } from "shared/cubiq-services";
import { Subject, Observable } from "rxjs";
import { BfxPopupEventSetting } from "../../bfx-user-settings/model/bfx-popup-event-setting";
import { BfxBlotterFilterService } from "../../bfx-blotter-filter/bfx-blotter-filter.service";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BFXNotificationType } from "../../../shared/cubiq-services/model/core/notifications/BFXNotificationType";
import { startWith } from "rxjs/operators";
import { NONE_FILTER, NONE_SOUND } from "app/bfx-user-settings/model/bfx-settings-constants";

export const PROFILE_KEY: string = ProfileSettingKeys.userProfileSettingsNotifPopup;
export const PROFILE_KEY_BMARK: string = ProfileSettingKeys.userProfileSettingsNotifBmarkPopup;

@Injectable()
export class BfxPopupNotificatonSettingsService {
  private primePromise: Promise<boolean>;
  private primed: boolean = false;
  private isAlgosEnabled: boolean = false;

  private activeUser: CubiqServices.ActiveUser;

  private existingFilters: Set<string> = new Set();

  private popupNotificationSettings: Array<BfxPopupEventSetting> = [];
  private popupNotifSettingsUpdateSubject: Subject<Array<BfxPopupEventSetting>> = new Subject();

  private bmarkPopupNotificationSettings: BfxPopupEventSetting[] = [];
  private bmarkPopupNotifSettingsUpdateSubject: Subject<Array<BfxPopupEventSetting>> =
    new Subject();

  constructor(
    private activeUserService: ActiveUserProfilePromiseService,
    protected filterService: BlotterFilterPromiseLookupService,
    private soundsService: BfxSoundsService,
    private translationService: TranslateService,
    private profilePromiseActionService: ProfilePromiseActionService,
    private blotterFilterilterService: BfxBlotterFilterService,
    private systemConfigService: SystemConfigPromiseService,
  ) {
    this.primePromise = new Promise<boolean>((resolve, reject) => {
      if (this.primed) {
        resolve(true);
      } else {
        this.activeUserService.getActiveUserData().then(
          (data: CubiqServices.ActiveUser) => {
            this.activeUser = data;
            this.filterService.getAll().then((filters) => {
              filters.forEach((filter) => this.existingFilters.add(filter.filterId.toString()));
              this.initializeNotificationSettings();
              this.blotterFilterilterService.listenForFilterDeleting().subscribe((ids) => {
                this.removeDeletedFilters(ids);
              });
              this.primed = true;
              resolve(true);
            });
          },
          (rjc) => {
            reject(rjc);
          },
        );
      }
    });
  }

  public getPopupNotificationsSettings(): Promise<Array<BfxPopupEventSetting>> {
    return new Promise<Array<BfxPopupEventSetting>>((res, rej) => {
      this.primePromise.then(
        (value: boolean) => {
          if (value) {
            res(this.popupNotificationSettings);
          } else {
            rej([]);
          }
        },
        (rjc) => {
          rej(rjc);
        },
      );
    });
  }

  public getBmarkPopupNotificationsSettings(): Promise<Array<BfxPopupEventSetting>> {
    return new Promise<Array<BfxPopupEventSetting>>((res, rej) => {
      this.primePromise.then(
        (value: boolean) => {
          if (value) {
            res(this.bmarkPopupNotificationSettings);
          } else {
            rej([]);
          }
        },
        (rjc) => {
          rej(rjc);
        },
      );
    });
  }

  /**
   * Get a stream of non-bmark popup settings primed with initial settings and all subsequent updates
   * @returns
   */
  public getPopupSettingsStream(): Observable<Array<BfxPopupEventSetting>> {
    return this.popupNotifSettingsUpdateSubject
      .asObservable()
      .pipe(startWith(this.popupNotificationSettings));
  }

  /**
   * Get a stream of bmark popup settings primed with initial settings and all subsequent updates
   * @returns
   */
  public getPopupBmarkSettingsStream(): Observable<Array<BfxPopupEventSetting>> {
    return this.bmarkPopupNotifSettingsUpdateSubject
      .asObservable()
      .pipe(startWith(this.bmarkPopupNotificationSettings));
  }

  /**
   * Get a stream of non-bmark popup settings updates - there is no priming
   * @returns
   */
  public getPopupSettingsUpdateStream(): Observable<Array<BfxPopupEventSetting>> {
    return this.popupNotifSettingsUpdateSubject.asObservable();
  }

  /**
   * Get a stream of bmark popup settings updates - there is no priming
   * @returns
   */
  public getPopupBmarkSettingsUpdateStream(): Observable<Array<BfxPopupEventSetting>> {
    return this.bmarkPopupNotifSettingsUpdateSubject.asObservable();
  }

  // load the popup notification settings, if they are not saved to the profile, we need to load a default per user, that last part still needs to be completed
  private initializeNotificationSettings(): void {
    //let notifEventsList: Array<string> = Object.keys(BFXNotificationType).filter(key => !isNaN(Number(BFXNotificationType[key])));
    if (this.activeUser.profileSettings && this.activeUser.profileSettings.has(PROFILE_KEY)) {
      let settingString: string = this.activeUser.profileSettings.get(PROFILE_KEY);
      if (settingString.length != 0) {
        this.popupNotificationSettings = this.parseJsonSetting(settingString);
        this.migrateAlgoSettings(this.popupNotificationSettings);
      }
    }
    const defaultSettings = this.getDefaultEventSettings();
    if (this.popupNotificationSettings.length == 0) {
      this.popupNotificationSettings = defaultSettings.concat();
    }

    // Update the settings if there's something
    this.popupNotificationSettings = this.checkAndUpdateNotificationSettings(
      this.popupNotificationSettings,
      defaultSettings,
    );

    if (this.activeUser.isSales()) {
      this.systemConfigService
        .hasRole(CubiqServices.Roles.VIEW_DEALER_INTERVENTION)
        .then((hasDealerInterventionRole: boolean) => {
          if (!hasDealerInterventionRole) {
            this.removeNotificationTypeFromPopupNotificationSettingsArray(
              BFXNotificationType.DEALER_INTERVENTION,
            );
          }
        });
    }

    this.systemConfigService
      .hasRole(CubiqServices.Roles.ROLE_VIEW_CONNECTION_STATUS)
      .then((hasConnectionMonitorRole: boolean) => {
        if (!hasConnectionMonitorRole) {
          this.removeNotificationTypeFromPopupNotificationSettingsArray(
            BFXNotificationType.CONNECTION_MONITOR,
          );
        }
      });

    this.profilePromiseActionService.getProfileConfigUpdates(PROFILE_KEY).subscribe((conf) => {
      this.popupNotificationSettings = this.parseJsonSetting(conf);
      this.popupNotifSettingsUpdateSubject.next(this.popupNotificationSettings);
    });

    // Bmark notification settings
    let bmarkPopupNotificationSettingString: string = null;
    if (this.activeUser.profileSettings && this.activeUser.profileSettings.has(PROFILE_KEY_BMARK)) {
      bmarkPopupNotificationSettingString = this.activeUser.profileSettings.get(PROFILE_KEY_BMARK);
    }
    if (bmarkPopupNotificationSettingString != null) {
      let bmarkNotifSettings = this.parseJsonSetting(bmarkPopupNotificationSettingString);
      this.bmarkPopupNotificationSettings =
        bmarkNotifSettings.length !== 0 ? bmarkNotifSettings : this.getDefaultBmarkEventSettings();
    } else {
      this.bmarkPopupNotificationSettings = this.getDefaultBmarkEventSettings();
    }

    this.profilePromiseActionService
      .getProfileConfigUpdates(PROFILE_KEY_BMARK)
      .subscribe((conf) => {
        this.bmarkPopupNotificationSettings = this.parseJsonSetting(conf);
        this.bmarkPopupNotifSettingsUpdateSubject.next(this.bmarkPopupNotificationSettings);
      });

    // Update the settings if there's something
    this.bmarkPopupNotificationSettings = this.checkAndUpdateNotificationSettings(
      this.bmarkPopupNotificationSettings,
      this.getDefaultBmarkEventSettings(),
    );

    // this.systemConfigService
    //   .getSystemConfig(CubiqServices.SysFeatures.algosEnabled)
    //   .then((isAlgosEnabled) => {
    //     this.isAlgosEnabled = isAlgosEnabled.toUpperCase() == "Y";
    //     if (!this.isAlgosEnabled) {
    //       this.removeNotificationTypeFromPopupNotificationSettingsArray(
    //         BFXNotificationType.UNSOLICITED_PAUSE_RESUME,
    //       );
    //     }
    //   });

    this.popupNotifSettingsUpdateSubject.next(this.popupNotificationSettings);
    this.bmarkPopupNotifSettingsUpdateSubject.next(this.bmarkPopupNotificationSettings);
  }

  /**
   * Function for comparing the notification settings lists, the one saved in the profile and the default in the system.
   * Returns the correct notification settings list if the default is updated in the future.
   * @param savedNotificationSettings of type `Array<BfxPopupEventSetting>`
   * @param defaultNotificationSettings of type `Array<BfxPopupEventSetting>`
   */
  private checkAndUpdateNotificationSettings(
    savedNotificationSettings: Array<BfxPopupEventSetting>,
    defaultNotificationSettings: Array<BfxPopupEventSetting>,
  ): Array<BfxPopupEventSetting> {
    let settings: BfxPopupEventSetting[] = [];
    let savedSettingsMap: any = {};
    let filledMigrate = true;
    let filledSavedSetting = null;

    savedNotificationSettings.forEach((setting: BfxPopupEventSetting) => {
      savedSettingsMap[setting.type] = setting;

      if (setting.type === BFXNotificationType.PART_FILLED) {
        filledMigrate = false;
      }

      if (setting.type === BFXNotificationType.FILLED) {
        filledSavedSetting = setting;
      }
    });

    if (filledMigrate) {
      savedSettingsMap[BFXNotificationType.PART_FILLED] = filledSavedSetting;
      savedSettingsMap[BFXNotificationType.ALGO_PART_FILLED] = filledSavedSetting;
    }

    for (let i: number = 0; i < defaultNotificationSettings.length; i++) {
      let setting: BfxPopupEventSetting = defaultNotificationSettings[i];

      if (savedSettingsMap[setting.type] != null) {
        let savedSetting: BfxPopupEventSetting = savedSettingsMap[setting.type];
        setting.enabled = savedSetting.enabled;
        setting.audio = savedSetting.audio;
        setting.disableAllAudio = savedSetting.disableAllAudio;
        setting.filter = savedSetting.filter;
      }

      settings.push(setting);
    }

    settings.sort((s1, s2) => s1.eventIdentifier.localeCompare(s2.eventIdentifier));

    return settings;
  }

  private removeNotificationTypeFromPopupNotificationSettingsArray(type: BFXNotificationType) {
    let indexToBeRemoved = this.popupNotificationSettings.findIndex((index) => index.type == type);
    this.popupNotificationSettings.splice(indexToBeRemoved, 1);
  }

  private parseJsonSetting(json: string): Array<any> {
    try {
      let result = [];
      let settingList: any = [];
      settingList = JSON.parse(json);
      if (settingList && settingList.length != 0) {
        settingList.forEach((event) => {
          let eventObj: BfxPopupEventSetting = new BfxPopupEventSetting(
            event.type,
            event.eventIdentifier,
            event.enabled,
            event.audio,
            event.disableAllAudio,
            event.filter,
            this.getBlotterType(event.type),
            event.isBmark,
          );
          this.checkFilterExist(eventObj);
          result.push(eventObj);
        });
        return result;
      }
    } catch (error) {
      logger.out.error("Error parsing popup settings: " + error);
    }
    return [];
  }

  /** If we load settings that do not have new algo settings included, then create
   * new algo settings based on their LIMIT base settings
   * @param appSettings
   */
  private migrateAlgoSettings(appSettings: BfxPopupEventSetting[]) {
    if (!appSettings.find((setting) => setting.type === BFXNotificationType.ALGO_ACTIVATED)) {
      // WE don't have the marker Algo setting so add all missing
      // Need to migrate algo settings from existing settings
      const appMap: Map<BFXNotificationType, BfxPopupEventSetting> = new Map();
      appSettings.forEach((setting) => appMap.set(setting.type, setting));
      if (appMap.has(BFXNotificationType.PART_FILLED)) {
        appMap.get(BFXNotificationType.PART_FILLED).blotterType = CubiqTS.BlotterType.TRADE;
      }
      if (appMap.has(BFXNotificationType.ALGO_PART_FILLED)) {
        appMap.get(BFXNotificationType.ALGO_PART_FILLED).blotterType = CubiqTS.BlotterType.TRADE;
      }
      const toMigrate = [
        {
          algoType: BFXNotificationType.ALGO_ACCEPTANCE_NEEDED,
          baseType: BFXNotificationType.ACCEPTANCE_NEEDED,
        },
        { algoType: BFXNotificationType.ALGO_ACTIVATED, baseType: BFXNotificationType.ACTIVATED },
        { algoType: BFXNotificationType.ALGO_ACTIVATING, baseType: BFXNotificationType.ACTIVATING },
        { algoType: BFXNotificationType.ALGO_CANCELLED, baseType: BFXNotificationType.CANCELLED },
        {
          algoType: BFXNotificationType.ALGO_DEAL_FAILURE,
          baseType: BFXNotificationType.DEAL_FAILURE,
        },
        { algoType: BFXNotificationType.ALGO_EXPIRED, baseType: BFXNotificationType.EXPIRED },
        { algoType: BFXNotificationType.ALGO_EXPIRING, baseType: BFXNotificationType.EXPIRING },
        {
          algoType: BFXNotificationType.ALGO_LIMITALGO_ORDER_ENTERED,
          baseType: BFXNotificationType.LIMITALGO_ORDER_ENTERED,
        },
        { algoType: BFXNotificationType.ALGO_FILLED, baseType: BFXNotificationType.FILLED },
        { algoType: BFXNotificationType.ALGO_MODIFIED, baseType: BFXNotificationType.MODIFIED },
        {
          algoType: BFXNotificationType.ALGO_PART_FILLED,
          baseType: BFXNotificationType.PART_FILLED,
        },
        {
          algoType: BFXNotificationType.ALGO_UNSOLICITED_CANCEL,
          baseType: BFXNotificationType.UNSOLICITED_CANCEL,
        },
        {
          algoType: BFXNotificationType.ALGO_UNSOLICITED_PAUSE_RESUME,
          baseType: BFXNotificationType.UNSOLICITED_PAUSE_RESUME,
        },
      ];
      for (const { algoType, baseType } of toMigrate) {
        if (!appMap.has(algoType)) {
          // Create algo default from specified setting with the base type
          const baseSetting = appMap.get(baseType);
          if (baseSetting) {
            const newSetting = Object.assign({}, baseSetting, { type: algoType });
            appSettings.push(newSetting);
          }
        }
      }
    }
  }

  private getDefaultEventSettings(): Array<BfxPopupEventSetting> {
    return [
      this.createDefaultEventSetting(BFXNotificationType.ACCEPTANCE_NEEDED),
      this.createDefaultEventSetting(BFXNotificationType.ACTIVATED),
      this.createDefaultEventSetting(BFXNotificationType.ACTIVATING),
      this.createDefaultEventSetting(BFXNotificationType.CANCELLED),
      this.createDefaultEventSetting(BFXNotificationType.EXPIRED),
      this.createDefaultEventSetting(BFXNotificationType.EXPIRING),
      this.createDefaultEventSetting(BFXNotificationType.PART_FILLED),
      this.createDefaultEventSetting(BFXNotificationType.FILLED),
      this.createDefaultEventSetting(BFXNotificationType.HOT),
      this.createDefaultEventSetting(BFXNotificationType.MODIFIED),
      this.createDefaultEventSetting(BFXNotificationType.STREAM_OFF),
      this.createDefaultEventSetting(BFXNotificationType.SUSPENDED),
      this.createDefaultEventSetting(BFXNotificationType.SUSPENDING),
      this.createDefaultEventSetting(BFXNotificationType.TRIGGERED),
      this.createDefaultEventSetting(BFXNotificationType.DEAL_FAILURE),
      this.createDefaultEventSetting(BFXNotificationType.DEALER_INTERVENTION),
      this.createDefaultEventSetting(BFXNotificationType.LIMITALGO_ORDER_ENTERED),
      this.createDefaultEventSetting(BFXNotificationType.UNSOLICITED_PAUSE_RESUME),
      this.createDefaultEventSetting(BFXNotificationType.UNSOLICITED_CANCEL),
      this.createDefaultEventSetting(BFXNotificationType.ALGO_INFLIGHT),
      this.createDefaultEventSetting(BFXNotificationType.CONNECTION_MONITOR),

      this.createDefaultEventSetting(BFXNotificationType.ALGO_ACCEPTANCE_NEEDED),
      this.createDefaultEventSetting(BFXNotificationType.ALGO_ACTIVATED),
      this.createDefaultEventSetting(BFXNotificationType.ALGO_ACTIVATING),
      this.createDefaultEventSetting(BFXNotificationType.ALGO_CANCELLED),
      this.createDefaultEventSetting(BFXNotificationType.ALGO_DEAL_FAILURE),
      this.createDefaultEventSetting(BFXNotificationType.ALGO_EXPIRED),
      this.createDefaultEventSetting(BFXNotificationType.ALGO_EXPIRING),
      this.createDefaultEventSetting(BFXNotificationType.ALGO_LIMITALGO_ORDER_ENTERED),
      this.createDefaultEventSetting(BFXNotificationType.ALGO_FILLED),
      this.createDefaultEventSetting(BFXNotificationType.ALGO_MODIFIED),
      this.createDefaultEventSetting(BFXNotificationType.ALGO_PART_FILLED),
      this.createDefaultEventSetting(BFXNotificationType.ALGO_UNSOLICITED_CANCEL),
      this.createDefaultEventSetting(BFXNotificationType.ALGO_UNSOLICITED_PAUSE_RESUME),
    ];
  }

  private getDefaultBmarkEventSettings(): Array<BfxPopupEventSetting> {
    return [
      this.createDefaultBmarkNotificationSetting(BFXNotificationType.BMARK_SOFT_CUT_OFF),
      this.createDefaultBmarkNotificationSetting(BFXNotificationType.BMARK_HARD_CUT_OFF),
      this.createDefaultBmarkNotificationSetting(BFXNotificationType.BMARK_TRIGGERED),
      this.createDefaultBmarkNotificationSetting(BFXNotificationType.BMARK_NEW_ORDERS),
      this.createDefaultBmarkNotificationSetting(BFXNotificationType.BMARK_DEAL_FAILURE),
      this.createDefaultBmarkNotificationSetting(BFXNotificationType.BMARK_CANCELLED),
      this.createDefaultBmarkNotificationSetting(BFXNotificationType.BMARK_ORDER_ENTERED),
      this.createDefaultBmarkNotificationSetting(BFXNotificationType.BMARK_MODIFIED),
      this.createDefaultBmarkNotificationSetting(BFXNotificationType.BMARK_FILLED),
    ];
  }

  private createDefaultBmarkNotificationSetting(type: BFXNotificationType): BfxPopupEventSetting {
    const eventLookupId = this.getEventId(type);
    if (
      type === BFXNotificationType.BMARK_NEW_ORDERS &&
      Array.isArray(this.popupNotificationSettings)
    ) {
      let acceptanceNeededSetting = this.popupNotificationSettings.find(
        (s) => s.type === BFXNotificationType.ACCEPTANCE_NEEDED,
      );

      if (acceptanceNeededSetting != null) {
        return new BfxPopupEventSetting(
          type,
          this.translationService.instant(eventLookupId),
          acceptanceNeededSetting.enabled, // IsNotifEnabled
          acceptanceNeededSetting.audio, // Audio
          acceptanceNeededSetting.disableAllAudio, // disableAllAudio
          { name: "None" }, // Filter,
          CubiqTS.BlotterType.BENCHMARK, // BlotterType
          true,
        );
      }
    }

    return new BfxPopupEventSetting(
      type,
      this.translationService.instant(eventLookupId),
      false, // IsNotifEnabled
      { label: "None" }, // Audio
      true, // disableAllAudio
      { name: "None" }, // Filter,
      CubiqTS.BlotterType.BENCHMARK, // BlotterType
      true,
    );
  }

  private createDefaultEventSetting(type: BFXNotificationType): BfxPopupEventSetting {
    const eventLookupId = this.getEventId(type);
    return new BfxPopupEventSetting(
      type,
      this.translationService.instant(eventLookupId),
      this.getIsNotifEnabled(type, this.activeUser.userTypeId),
      this.getAudio(type, this.activeUser.userTypeId),
      this.getdisableAllAudio(type, this.activeUser.userTypeId),
      this.getFilter(type, this.activeUser.userTypeId),
      this.getBlotterType(type),
    );
  }

  /**
   * Get the text lookup in en.json for the specified notification type
   * @param type
   * @returns
   */
  private getEventId(type: BFXNotificationType) {
    switch (type) {
      case CubiqServices.BFXNotificationType.ACCEPTANCE_NEEDED:
      case CubiqServices.BFXNotificationType.ALGO_ACCEPTANCE_NEEDED:
      case CubiqServices.BFXNotificationType.BMARK_NEW_ORDERS:
        return "notification.event.id.acceptance_needed";
      default:
        return BFXNotificationType[type];
    }
  }

  // get default value for whether notification is on or off by user type and notification type
  private getIsNotifEnabled(type: BFXNotificationType, userType: CubiqTS.UserType): boolean {
    if (type == BFXNotificationType.UNSOLICITED_PAUSE_RESUME) {
      return false;
    }
    switch (userType) {
      case CubiqTS.UserType.TRADER:
        return (
          type === BFXNotificationType.ACCEPTANCE_NEEDED ||
          type === BFXNotificationType.FILLED ||
          type === BFXNotificationType.ALGO_ACCEPTANCE_NEEDED ||
          type === BFXNotificationType.ALGO_FILLED ||
          type === BFXNotificationType.STREAM_OFF ||
          type === BFXNotificationType.TRIGGERED
        );
      case CubiqTS.UserType.SALES:
        return (
          type === BFXNotificationType.FILLED ||
          type === BFXNotificationType.DEAL_FAILURE ||
          type === BFXNotificationType.ALGO_FILLED ||
          type === BFXNotificationType.ALGO_DEAL_FAILURE ||
          type === BFXNotificationType.DEALER_INTERVENTION
        );
      case CubiqTS.UserType.ADMIN:
        return (
          type === BFXNotificationType.STREAM_OFF ||
          type === BFXNotificationType.DEAL_FAILURE ||
          type === BFXNotificationType.ALGO_DEAL_FAILURE
        );
      case CubiqTS.UserType.CLIENT:
        return type === BFXNotificationType.FILLED || type === BFXNotificationType.ALGO_FILLED;
    }
    return false;
  }
  private getdisableAllAudio(type: BFXNotificationType, userType: CubiqTS.UserType): boolean {
    // Must be constant
    return false;
  }

  // get default value for whether notification is on or off by user type and notification type
  private getAudio(type: BFXNotificationType, userType: CubiqTS.UserType) {
    switch (userType) {
      case CubiqTS.UserType.TRADER:
        if (
          type === BFXNotificationType.ACCEPTANCE_NEEDED ||
          type === BFXNotificationType.ALGO_ACCEPTANCE_NEEDED ||
          type === BFXNotificationType.TRIGGERED
        ) {
          return { label: "Klaxon" };
        }
        break;
      case CubiqTS.UserType.SALES:
        if (type === BFXNotificationType.DEALER_INTERVENTION) {
          return { label: "Klaxon" };
        }
        break;
      case CubiqTS.UserType.ADMIN:
        if (
          type === BFXNotificationType.STREAM_OFF ||
          type === BFXNotificationType.DEAL_FAILURE ||
          type === BFXNotificationType.ALGO_DEAL_FAILURE
        ) {
          return { label: "Klaxon" };
        }
        break;
      case CubiqTS.UserType.CLIENT:
        if (type === BFXNotificationType.FILLED || type === BFXNotificationType.ALGO_FILLED) {
          return { label: "Klaxon" };
        }
        break;
    }
    return NONE_SOUND;
  }

  // get default value for whether notification is on or off by user type and notification type
  private getFilter(type: BFXNotificationType, userType: CubiqTS.UserType) {
    return NONE_FILTER;
  }

  private getBlotterType(type: BFXNotificationType): CubiqTS.BlotterType {
    switch (type) {
      case BFXNotificationType.ALGO_ACCEPTANCE_NEEDED:
      case BFXNotificationType.ALGO_ACTIVATED:
      case BFXNotificationType.ALGO_ACTIVATING:
      case BFXNotificationType.ALGO_CANCELLED:
      case BFXNotificationType.ALGO_EXPIRED:
      case BFXNotificationType.ALGO_EXPIRING:
      case BFXNotificationType.ALGO_LIMITALGO_ORDER_ENTERED:
      case BFXNotificationType.ALGO_MODIFIED:
      case BFXNotificationType.ALGO_UNSOLICITED_CANCEL:
      case BFXNotificationType.ALGO_UNSOLICITED_PAUSE_RESUME:

      case BFXNotificationType.ACCEPTANCE_NEEDED:
      case BFXNotificationType.ACTIVATED:
      case BFXNotificationType.ACTIVATING:
      case BFXNotificationType.CANCELLED:
      case BFXNotificationType.EXPIRED:
      case BFXNotificationType.EXPIRING:
      case BFXNotificationType.HOT:
      case BFXNotificationType.MODIFIED:
      case BFXNotificationType.SUSPENDED:
      case BFXNotificationType.SUSPENDING:
      case BFXNotificationType.TRIGGERED:
      case BFXNotificationType.UNSOLICITED_PAUSE_RESUME:
      case BFXNotificationType.LIMITALGO_ORDER_ENTERED:
        return CubiqTS.BlotterType.ORDER;
      case BFXNotificationType.ALGO_PART_FILLED:
      case BFXNotificationType.ALGO_DEAL_FAILURE:
      case BFXNotificationType.ALGO_FILLED:
      case BFXNotificationType.FILLED:
      case BFXNotificationType.PART_FILLED:
      case BFXNotificationType.DEAL_FAILURE:
        return CubiqTS.BlotterType.TRADE;
      case BFXNotificationType.DEALER_INTERVENTION:
        return CubiqTS.BlotterType.INTERVENTION;
      case BFXNotificationType.CONNECTION_MONITOR:
        return null;
      case BFXNotificationType.BMARK_SOFT_CUT_OFF:
      case BFXNotificationType.BMARK_HARD_CUT_OFF:
      case BFXNotificationType.BMARK_TRIGGERED:
      case BFXNotificationType.BMARK_NEW_ORDERS:
      case BFXNotificationType.BMARK_DEAL_FAILURE:
      case BFXNotificationType.BMARK_CANCELLED:
      case BFXNotificationType.BMARK_MODIFIED:
      case BFXNotificationType.BMARK_FILLED:
      case BFXNotificationType.BMARK_ORDER_ENTERED:
        return CubiqTS.BlotterType.BENCHMARK;
    }
    return CubiqTS.BlotterType.ORDER;
  }

  private checkFilterExist(eventObj: BfxPopupEventSetting) {
    if (!this.existingFilters.has(eventObj.filter.id)) {
      eventObj.filter = { name: "None", id: "0", blotterType: 0 };
    }
  }

  private removeDeletedFilters(ids: string[]) {
    ids.forEach((filterId) => {
      this.existingFilters.delete(filterId);
      let popupEventObject: BfxPopupEventSetting = this.popupNotificationSettings.find((a) => {
        return a.filter.id == filterId;
      });
      if (popupEventObject != null)
        popupEventObject.filter = { name: "None", id: "0", blotterType: 0 };
    });
  }
}
